import React, { useCallback, useState } from "react";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import { cn } from "@/lib/utils";
import type { StrategyRdLogicPath } from "../../../types/strategyRd";
import {
  strategyRdBrandActionButtonClassName,
  strategyRdMetaClassName,
  strategyRdSectionTitleClassName,
} from "./strategyRdCardShell";

export type StrategyRdLogicPathPanelProps = {
  logicPath: StrategyRdLogicPath | null | undefined;
  className?: string;
};

function LogicLine({ line }: { line: string }): JSX.Element {
  return (
    <li className="flex gap-2">
      <span
        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-[color:var(--brand-tropical-surf)]"
        aria-hidden
      />
      <span className={cn(strategyRdMetaClassName, "text-gray-500 dark:text-white")}>{line}</span>
    </li>
  );
}

function BranchCard({
  title,
  accentClassName,
  lines,
}: {
  title: string;
  accentClassName: string;
  lines: string[];
}): JSX.Element {
  return (
    <div
      className={cn(
        "min-w-0 rounded-[6px] border border-[color:var(--brand-tropical-surf)]/25 bg-background/90 px-3 py-2.5",
        "border-l-[3px]",
        accentClassName
      )}
    >
      <p className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{title}</p>
      <ul className="mt-2 space-y-1.5">
        {lines.map((line) => (
          <LogicLine key={`${title}-${line}`} line={line} />
        ))}
      </ul>
    </div>
  );
}

function SummaryBlock({ lines }: { lines: string[] }): JSX.Element {
  if (lines.length === 0) return <></>;
  const [first, ...rest] = lines;
  const readingLead =
    first.trim().toLowerCase().startsWith("reading:") ? first.trim() : null;
  const ifLines = readingLead ? rest : lines;

  return (
    <div
      className={cn(
        "rounded-[6px] border border-[color:var(--brand-tropical-surf)]/30 bg-background/90 px-3 py-2.5"
      )}
    >
      <p className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Summary</p>
      {readingLead ? (
        <p className="mt-2 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
          {readingLead}
        </p>
      ) : null}
      {ifLines.length > 0 ? (
        <ul className={cn("space-y-1.5", readingLead ? "mt-2" : "mt-2")}>
          {ifLines.map((line) => (
            <LogicLine key={line} line={line} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** BE-authored human recipe — hide entirely when logic_path is absent. Never rewrite strings. */
export function StrategyRdLogicPathPanel({
  logicPath,
  className,
}: StrategyRdLogicPathPanelProps): JSX.Element | null {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    const text = logicPath?.text;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  }, [logicPath?.text]);

  if (!logicPath) return null;
  const hasNotes = logicPath.notes.length > 0;
  const hasSummary = logicPath.summary.length > 0;
  const hasLong = logicPath.long.length > 0;
  const hasShort = logicPath.short.length > 0;
  const text = logicPath.text.trim();
  if (!hasNotes && !hasSummary && !hasLong && !hasShort && !text) return null;

  const showTextFallback = Boolean(text) && !hasNotes && !hasSummary && !hasLong && !hasShort;

  return (
    <div
      data-strategy-rd-logic-path=""
      className={cn(
        "flex flex-col gap-3 rounded-[6px] border border-[color:var(--brand-tropical-surf)]/40 px-3 py-3",
        "bg-[color-mix(in_oklab,var(--brand-tropical-surf)_8%,transparent)]",
        "dark:bg-[color-mix(in_oklab,var(--brand-tropical-surf)_14%,transparent)]",
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className={strategyRdSectionTitleClassName}>Logic path</h3>
          <p className={cn(strategyRdMetaClassName, "mt-0.5")}>
            Reading and branches from your current recipe. Not from individual matches.
          </p>
        </div>
        {text ? (
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            className={strategyRdBrandActionButtonClassName}
            onClick={() => void onCopy()}
          >
            {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
            {copied ? "Copied" : "Copy"}
          </Button>
        ) : null}
      </div>

      {hasNotes ? (
        <div
          className={cn(
            "rounded-[6px] border border-[color:var(--brand-tropical-surf)]/30 bg-background/90 px-3 py-2.5"
          )}
        >
          <p className={STRATEGY_PINE_SECTION_LABEL_CLASS}>How we read your settings</p>
          <ul className="mt-2 space-y-1.5">
            {logicPath.notes.map((line) => (
              <LogicLine key={line} line={line} />
            ))}
          </ul>
        </div>
      ) : null}

      {hasSummary ? <SummaryBlock lines={logicPath.summary} /> : null}

      {hasLong || hasShort ? (
        <div
          className={cn(
            "grid grid-cols-1 gap-2",
            hasLong && hasShort ? "md:grid-cols-2" : ""
          )}
        >
          {hasLong ? (
            <BranchCard
              title="For Long"
              accentClassName="border-l-[color:var(--brand-tropical-surf)]"
              lines={logicPath.long}
            />
          ) : null}
          {hasShort ? (
            <BranchCard
              title="For Short"
              accentClassName="border-l-[color:var(--brand-tropical-surf)]"
              lines={logicPath.short}
            />
          ) : null}
        </div>
      ) : null}

      {showTextFallback ? (
        <pre
          className={cn(
            "whitespace-pre-wrap rounded-[6px] border border-[color:var(--brand-tropical-surf)]/25",
            "bg-background/90 p-3 font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white"
          )}
        >
          {text}
        </pre>
      ) : null}
    </div>
  );
}
